import { Chart, ChartNodeData } from "@/components/chart/Chart.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { RightPaneLayout } from "@/components/layout/RightPaneLayout.tsx";
import useDepartmentsChartHook from "@/hooks/use-departments-chart.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { DepartmentViewRightPane } from "@/pages/employees/departments-chart/components/DepartmentViewRightPane.tsx";
import { ApiDepartmentsChartFilter } from "@/pages/employees/departments-chart/departments-chart.types.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { ApiDepartment, DepartmentNodeMode } from "@/types/department.types.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { Avatar, Page } from "@skailer-com/uikit";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export function DepartmentsChart() {
  const { t } = useTranslation();
  const { data: employee } = useEmployeeMe();
  const [chart, setChart] = useState<ChartNodeData<ApiDepartment>[]>([]);
  const [searchParams] = useSearchParams();
  const [expanded, setExpanded] = useState<Set<string>>(new Set([]));
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | undefined>(undefined);
  const { renderCaption, renderExtra, loadDepartmentsChart, loadDepartmentsChartByNodeId } =
    useDepartmentsChartHook();
  const library = filtersLibrary();
  const { canCurrentEmployeeViewField } = usePermissions();

  const baseEmployeeFilters = [
    library.division_ids,
    library.location_ids,
    library.employment_type_ids,
    library.employment_contract_ids,
    library.legal_entity_ids,
    ...(canCurrentEmployeeViewField(EmployeeFieldPermission.employee_lifecycle_status)
      ? [
          {
            ...library.employee_lifecycle_statuses,
            hideInFilter: true,
          },
        ]
      : []),
  ];

  const {
    filter: { filter },
    filterButton,
    isFilterReady,
  } = useAppFilter<ApiDepartmentsChartFilter>({
    filters: [...baseEmployeeFilters],
    source: AnalyticReportTypes.employeesChart,
  });

  useTitle(t("core:departments"));

  const appendDepartmentsToChart = useCallback(
    (departments: ApiDepartment[]) => {
      setChart((prevChart) => {
        const newEntitiesIds: Set<number | undefined> = new Set(
          departments.map((item) => item?.id)
        );
        const nodes: ChartNodeData<ApiDepartment>[] = [];

        const prev = [...prevChart]
          .filter((item) => item.original && !newEntitiesIds.has(item.original.id))
          .map((item) => item.original);

        const mergedItems = [...prev, ...departments];
        const mergedIds: Set<number | undefined> = new Set(mergedItems.map((item) => item?.id));
        let needRoot = false;
        let countRootElements = 0;

        mergedItems.forEach((chartItem) => {
          if (!chartItem) return;

          const parentIdNumber: number | null = chartItem.parent_id ?? null;

          if (!parentIdNumber) {
            needRoot = true;
            countRootElements++;
          }
        });

        mergedItems.forEach((chartItem) => {
          if (!chartItem) return;

          const parentIdNumber: number | null = chartItem.parent_id ?? null;

          let parentId: string | null = parentIdNumber ? `${parentIdNumber}` : "root";
          let isExpandUpNode = false;

          if (parentIdNumber && !mergedIds.has(parentIdNumber) && !needRoot) {
            parentId = null;
            isExpandUpNode = true;
          }

          nodes.push({
            title: chartItem.title,
            caption: renderCaption(chartItem),
            extra: renderExtra(chartItem),
            imgUrl: chartItem.manager ? getEmployeeAvatarUrl(chartItem.manager, "md") : null,
            id: `${chartItem.id}`,
            avatarElement:
              chartItem.image_url || chartItem.emoji ? (
                <Avatar url={chartItem.image_url ?? null} emoji={chartItem.emoji} size={"xl"} />
              ) : undefined,
            parentId: parentId,
            count: chartItem.direct_subordinates_count,
            isRoot: false,
            isExpandUpNode,
            original: chartItem,
          });
        });

        if (needRoot) {
          nodes.push({
            title: employee?.organization?.title ?? t("common:company"),
            imgUrl: employee?.organization?.logo_url ?? "/favicon.svg",
            id: "root",
            parentId: null,
            isExpandUpNode: false,
            count: countRootElements,
            isRoot: true,
          });
        }

        return nodes;
      });
    },
    [chart]
  );

  useEffect(() => {
    if (!isFilterReady) return;

    const initializeChart = async () => {
      setChart([]);
      const departmentIdParam = searchParams.get("department_id");
      const departmentId = departmentIdParam ? Number(departmentIdParam) : undefined;
      const departments = await loadDepartmentsChart(
        departmentId,
        filter,
        departmentId ? DepartmentNodeMode.parent : DepartmentNodeMode.children
      );

      if (departmentId) {
        const opened: string[] = [];
        const current: ApiDepartment | undefined = departments?.find(
          (dep) => dep.id === departmentId
        );

        opened.push(`${departmentId}`);
        if (current && current.parent_id) opened.push(`${current.parent_id}`);

        setExpanded(new Set(opened));

        if (departments?.some((department) => !department.parent_id)) {
          const rootItems = await loadDepartmentsChart(undefined, filter);

          if (rootItems) appendDepartmentsToChart(rootItems);

          if (current?.parent_id) {
            const rootDepartmentItems = await loadDepartmentsChart(current.parent_id, filter);

            if (rootDepartmentItems) appendDepartmentsToChart(rootDepartmentItems);
          }
        }
      }

      if (departments) appendDepartmentsToChart(departments);
    };

    initializeChart();
  }, [isFilterReady, searchParams]);

  const onNodeClick = useCallback((nodeId: string) => {
    if (nodeId == "root") return;

    const departmentId = Number(nodeId);
    if (Number.isNaN(departmentId)) return;

    setSelectedDepartmentId(departmentId);
  }, []);

  const onExpandClick = useCallback(
    (nodeId: string, needLoadData: boolean) => {
      if (!needLoadData) return;

      loadDepartmentsChartByNodeId(nodeId, filter).then((departments) => {
        if (departments) appendDepartmentsToChart(departments);
      });
    },
    [chart]
  );

  const onExpandUpClick = useCallback(
    (nodeId: string) => {
      loadDepartmentsChartByNodeId(nodeId, filter, DepartmentNodeMode.parent).then((items) => {
        items.forEach((item) => {
          if (nodeId == `${item.id}` && item.parent_id) {
            setExpanded((prevState) => prevState.add(`${item.parent_id}`));
          }
        });

        if (items) appendDepartmentsToChart(items);

        if (!items.some((item) => !item.parent_id)) return;

        const aimDepartment = items.find((department) => `${department.id}` == nodeId);
        const rootDepartmentId = aimDepartment?.parent_id;

        loadDepartmentsChart(undefined, filter).then((rootItems) => {
          if (rootItems) appendDepartmentsToChart(rootItems);
        });

        if (rootDepartmentId) {
          loadDepartmentsChart(rootDepartmentId, filter).then((rootDepartmentItems) => {
            if (rootDepartmentItems) appendDepartmentsToChart(rootDepartmentItems);
          });
        }
      });
    },
    [chart]
  );

  const selectedDepartment = selectedDepartmentId
    ? chart.find((item) => item.id == `${selectedDepartmentId}`)?.original
    : undefined;

  const hideRightPane = useCallback(() => {
    setSelectedDepartmentId(undefined);
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.departments.viewedChart);
  });

  return (
    <Page variant="opaque" className={"h-full"}>
      <RightPaneLayout
        className={"h-full"}
        onHideRightPane={hideRightPane}
        rightPane={
          selectedDepartment
            ? (hidePane) => (
                <DepartmentViewRightPane
                  key={selectedDepartmentId}
                  department={selectedDepartment}
                  employeeFilter={{
                    ...filter,
                    page_number: 1,
                  }}
                  onClose={hidePane}
                />
              )
            : undefined
        }
      >
        <Page.Header title={t("core:departments")}>{filterButton()}</Page.Header>
        {chart.length > 0 && (
          <Chart
            data={chart}
            onNodeClick={onNodeClick}
            expandedSet={expanded}
            onExpandClick={onExpandClick}
            onExpandUpClick={onExpandUpClick}
          />
        )}
      </RightPaneLayout>
    </Page>
  );
}
